import axios from 'axios';

const api = axios.create({
  baseURL: '/api',
  withCredentials: true,
});

// Auto-refresh on 401
const SKIP_REFRESH_URLS = ['/auth/me', '/auth/refresh', '/auth/login'];

api.interceptors.response.use(
  (res) => res,
  async (err) => {
    const original = err.config;
    const url = original?.url ?? '';
    const isSkipped = SKIP_REFRESH_URLS.some((u) => url.includes(u));
    const onLoginPage = window.location.pathname === '/login';

    if (err.response?.status === 401 && !original._retry && !isSkipped && !onLoginPage) {
      original._retry = true;
      try {
        await axios.post('/api/auth/refresh', {}, { withCredentials: true });
        return api(original);
      } catch {
        window.location.href = '/login';
      }
    }
    return Promise.reject(err);
  }
);

export default api;

// ─── Auth ─────────────────────────────────────────────────────────────────────
export const authApi = {
  login: (username, password) => api.post('/auth/login', { username, password }),
  logout: () => api.post('/auth/logout'),
  me: () => api.get('/auth/me'),
  changePassword: (current_password, new_password) =>
    api.post('/auth/change-password', { current_password, new_password }),
};

// ─── Spotify ──────────────────────────────────────────────────────────────────
export const spotifyApi = {
  status: () => api.get('/spotify/status'),
  connect: () => { window.location.href = '/api/spotify/connect'; },
  disconnect: () => api.delete('/spotify/disconnect'),
  currentSong: () => api.get('/spotify/current-song'),
  search: (q) => api.get('/spotify/search', { params: { q } }),
  addToQueue: (spotify_uri) => api.post('/spotify/add-to-queue', { spotify_uri }),
};

export const spotifySettingsApi = {
  get: () => api.get('/spotify/settings/'),
  update: (data) => api.put('/spotify/settings/', data),
  getPlaylists: () => api.get('/spotify/settings/playlists'),
  createPlaylist: (name) => api.post('/spotify/settings/playlists', { name }),
};

// ─── Twitch ───────────────────────────────────────────────────────────────────
export const twitchApi = {
  status: () => api.get('/twitch/status'),
  connect: (type = 'main') => { window.location.href = `/api/twitch/connect?type=${type}`; },
  disconnect: (type = 'main') => api.delete(`/twitch/disconnect?type=${type}`),
  channelPoints: () => api.get('/twitch/channel-points'),
};

export const twitchSettingsApi = {
  get: () => api.get('/twitch/settings/'),
  update: (data) => api.put('/twitch/settings/', data),
};

// ─── Widget ───────────────────────────────────────────────────────────────────
export const widgetApi = {
  getConfig: () => api.get('/widget/config'),
  updateConfig: (config) => api.put('/widget/config', config),
  obsUrl: () => api.get('/widget/obs-url'),
  rotateUuid: () => api.post('/widget/rotate-uuid'),
};

// ─── Queue ────────────────────────────────────────────────────────────────────
export const queueApi = {
  list: () => api.get('/queue/'),
  add: (data) => api.post('/queue/add', data),
  remove: (id) => api.delete(`/queue/${id}`),
  skip: () => api.post('/queue/skip'),
  reorder: (ordered_ids) => api.put('/queue/reorder', { ordered_ids }),
};

// ─── Bot ──────────────────────────────────────────────────────────────────────
export const botApi = {
  status: () => api.get('/bot/status'),
  start: () => api.post('/bot/start'),
  stop: () => api.post('/bot/stop'),
  getSettings: () => api.get('/bot/settings'),
  updateSettings: (data) => api.put('/bot/settings', data),
};

// ─── Admin ────────────────────────────────────────────────────────────────────
export const adminApi = {
  listUsers: () => api.get('/admin/users'),
  createUser: (data) => api.post('/admin/users', data),
  updateUser: (id, data) => api.put(`/admin/users/${id}`, data),
  deleteUser: (id) => api.delete(`/admin/users/${id}`),
  resetPassword: (id, new_password) =>
    api.put(`/admin/users/${id}/reset-password`, { new_password }),
  systemBot: () => api.get('/admin/system-bot'),
  connectSystemBot: () => { window.location.href = '/api/twitch/connect?type=system'; },
  disconnectSystemBot: () => api.delete('/admin/system-bot'),
};
